import { useState, useEffect } from "react";
import { useHistory } from "react-router";

import { ExceptionOutlined } from "@ant-design/icons";
import { Divider, Select, Table } from "antd";
import Title from "antd/lib/typography/Title";
import _ from "lodash";

import { ErrorResponse } from "utils/types";
import notifier from "utils/notifier";
import { DocType, DocumentCategory } from "utils/enums";
import { formatDate, handleDownload, getDisplayCompanyId, getMimeType } from "utils/helpers";

import DocCard from "components/Documents/DocCard";

import { CardGroupProps } from "./Proptypes";

const CardGroup = (props: CardGroupProps) => {

  const history = useHistory();
  useEffect(() => {
    getUnreadCount();
    getMostRecentDocsForMemDocPage();
  }, []);
  const [unreadDocuments, setUnreadDocuments] = useState<any>([]);
  const [latestDocumentsDataSource, setLatestDocumentsDataSource] = useState<Object[]>([]);
  const [collapseState, setCollapseState] = useState<any>([]);
  const [specialties, setSpecialties] = useState<any>([]);
  const [providerNames, setProviderNames] = useState<any>([]);

  const [defaultSpecOptions, setDefaultSpecOptions] = useState<any>([]);
  const [defaultProviderNamesOptions, setDefaultProviderNamesOptions] = useState<any>([]);

  const latestDocumentsColumns = [
    {
      title: "IPA",
      key: "RmiCompanyId",
      dataIndex: "RmiCompanyId",
      render: (text: string) => getDisplayCompanyId(text),
      sorter: (a: any, b: any) => a.RmiCompanyId.localeCompare(b.RmiCompanyId),
    },
    {
      title: "Provider",
      key: "RmiProvName",
      dataIndex: "RmiProvName",
      sorter: (a: any, b: any) => a.RmiProvName.localeCompare(b.RmiProvName),
    },
    {
      title: "File Name",
      key: "RmiOrigFileName",
      dataIndex: "RmiOrigFileName",
      render: (_: string, record: any) => <a onClick={() => [DocumentCategory.MEMOS, DocumentCategory.MISC].includes(record.RmiDocumentCategory) ? downloadFile(record, record.RmiDocumentCategory) : downloadOldDocuments(record, record.RmiDocumentCategory)}>{record.RmiOrigFileName}</a>,
      sorter: (a: any, b: any) => a.RmiOrigFileName.localeCompare(b.RmiOrigFileName),
    },
    {
      title: "Date Received",
      key: "RmiUploadDate",
      dataIndex: "RmiUploadDate",
      render: (_: string, record: any) => formatDate(record.RmiUploadDate, 'MM/DD/YYYY [at] h:mmA'),
      sorter: (a: any, b: any) => a.RmiUploadDate.localeCompare(b.RmiUploadDate),
    },
    {
      title: "Section",
      key: "RmiDocumentCategory",
      dataIndex: "RmiDocumentCategory",
      sorter: (a: any, b: any) => a.RmiDocumentCategory.localeCompare(b.RmiDocumentCategory),
    },
    {
      title: "Read Date",
      key: "RmiReadDate",
      dataIndex: "RmiReadDate",
      render: (_: string, record: any) => formatDate(record.RmiReadDate, 'MM/DD/YYYY [at] h:mmA'),
      sorter: (a: any, b: any) =>  {
        a = a.RmiReadDate || '';
        b = b.RmiReadDate || '';
        return a.localeCompare(b);
      },
    },
  ];

  //get the unread document counts
  const getUnreadCount = () => {
    return props.getGroupsAndCategories(sessionStorage.user_id)
      .then((response: any) => {
        setUnreadDocuments(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const getMostRecentDocsForMemDocPage = () => {
    return props.getMostRecentDocsForMemDocPage(sessionStorage.user_id, 15)
      .then((response: any) => {
        const latestDocuments: any = [];
        response.forEach((value: any, index: number) => {
          latestDocuments.push({
            ...value,
              key: index
          });
        });

        setLatestDocumentsDataSource(latestDocuments);
        getUnreadCount();
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  //Download Documents API when you click on the links on the table
  //document too long to define
  const downloadOldDocuments = (documentFile: any, pacType: string) => {
    return props.downloadMemberDocument(documentFile.RmiFilePath, documentFile.RmiFileName, documentFile.RmiUid)
      .then((response: any) => {
        handleDownload(response, documentFile.RmiFileName);
        updateReadInfo(document, pacType);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      });
  }

  //Update Read Info API after you click on download member document link
  const updateReadInfo = (document: any, pacType: string) => {
    return props.updateReadInfo(sessionStorage.user_id, document.RmiUid, pacType)
      .then(() => {
        getMostRecentDocsForMemDocPage();
        getUnreadCount();
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  const handleRowExpand = (expanded: any, record: any) => {
    const keys = [];
    if (expanded) {
      props.getProviderByFileId(record.RmiUid)
        .then((response: any) => {
          const defaultProviderNames: any = [];
          const defaultOptions: any = [];
          for (const value of response) {
            defaultProviderNames.push(`${value.RmiRevFullName} - [${value.RmiProvId}]`);
            defaultOptions.push({
              value: `${value.RmiRevFullName} - [${value.RmiProvId}]`
            })
          }
          setProviderNames(defaultProviderNames);
          setDefaultProviderNamesOptions(defaultOptions);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });

      props.getSpecByFileId(record.RmiUid)
        .then((response: any) => {
          const specCodes: any = [];
          const options: any = [];
          for (const value of response) {
            specCodes.push(`${value.RmiDescr} - [${value.RmiCode}]`);
            options.push({
              value: `${value.RmiDescr} - [${value.RmiCode}]`
            });
          }

          setSpecialties(specCodes);
          setDefaultSpecOptions(options);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });

      keys.push(record.key);
    }
    setCollapseState(keys);
  }

  const gotoUrl = (url: string) => {
    history.push(url);
  };

  const downloadFile = (record: any, pacType: string) => {
    props.downloadFile(record.RmiCompanyId, record.RmiFilePath)
      .then((response: string) => {
        const mimeType = getMimeType(record.RmiFilePath);
        let blob = new Blob([response], { type: mimeType });
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, "_blank", "");
        updateReadInfo(record, pacType);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  return (
    <>
      <div className="row flex-lg-wrap justify-content-center mb-2">
        <DocCard
          title={DocumentCategory.MEMOS}
          docCount={unreadDocuments.length > 0 ? unreadDocuments[6].RmiNewDocCount : 0}
          docType={DocType.DOCUMENTS}
          icon={<ExceptionOutlined />}
          loading={props.isFetchingGroups}
          onClick={() => gotoUrl('/documents/memos')}
        />
        <DocCard
          title={DocumentCategory.CONSULT_NOTES}
          docCount={unreadDocuments.length > 0 ? unreadDocuments[1].RmiNewDocCount : 0}
          docType={DocType.DOCUMENTS}
          icon={<ExceptionOutlined />}
          loading={props.isFetchingGroups}
          onClick={() => gotoUrl('/documents/consult-notes')}
        />
        <DocCard
          title={DocumentCategory.CASE_DOCUMENTS}
          docCount={unreadDocuments.length > 0 ? unreadDocuments[2].RmiNewDocCount : 0}
          docType={DocType.DOCUMENTS}
          icon={<ExceptionOutlined />}
          loading={props.isFetchingGroups}
          onClick={() => gotoUrl('/documents/case-documents')}
        />
        <DocCard
          title={DocumentCategory.CCS_AGING_OUT_DOCUMENTS}
          docCount={unreadDocuments.length > 0 ? unreadDocuments[3].RmiNewDocCount : 0}
          docType={DocType.DOCUMENTS}
          icon={<ExceptionOutlined />}
          loading={props.isFetchingGroups}
          onClick={() => gotoUrl('/documents/ccs-aging')}
        />
        <DocCard
          title={DocumentCategory.RESCIND_NOTICE}
          docCount={unreadDocuments.length > 0 ? unreadDocuments[4].RmiNewDocCount : 0}
          docType={DocType.DOCUMENTS}
          icon={<ExceptionOutlined />}
          loading={props.isFetchingGroups}
          onClick={() => gotoUrl('/documents/rescind-notice')}
        />
        {/* -- Hiding the Member Roster Card from New Documents Panel (Story 51911) --
        <DocCard
          title={DocumentCategory.MEMBER_ROSTER}
          docCount={unreadDocuments.length > 0 ? unreadDocuments[5].RmiNewDocCount : 0}
          docType={DocType.DOCUMENTS}
          icon={<ExceptionOutlined />}
          loading={props.isFetchingGroups}
          onClick={() => gotoUrl('/documents/member-roster')}
        /> */}
        <DocCard
          title={DocumentCategory.MISC}
          docCount={unreadDocuments.length > 0 ? unreadDocuments[7].RmiNewDocCount : 0}
          docType={DocType.DOCUMENTS}
          icon={<ExceptionOutlined />}
          loading={props.isFetchingGroups}
          onClick={() => gotoUrl('/documents/misc')}
        />
      </div>

      {/*<Divider />
      <div className="flex-lg-wrap mt-2 mb-2">
        <Title level={5}>Latest Fifteen Documents</Title>
        <Table
          columns={latestDocumentsColumns}
          dataSource={latestDocumentsDataSource}
          pagination={{ pageSize: 15, hideOnSinglePage: true  }}
          loading={props.isFetchingLatestDocuments}
          expandable={{
            expandedRowRender: record =>
            (<>
              <div className="row">
                <div className="col-lg-12">
                  <Title level={5}>Providers associated with this document</Title>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    options={defaultProviderNamesOptions}
                    value={providerNames}
                    filterOption={false}
                    disabled
                  />
                </div>
                <div className="col-lg-12">
                  <Title level={5}>Specialties associated with this document</Title>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    options={defaultSpecOptions}
                    value={specialties}
                    filterOption={false}
                    disabled
                  />
                </div>
              </div>
              </>),
          }}
          onExpand={(expanded, record) => handleRowExpand(expanded, record)}
          expandedRowKeys={collapseState}
        />
      </div>*/}

    </>
  );
};

export default CardGroup;
